import React, { useEffect, useState } from 'react';
import { Box, Container, List, ListItem, ListItemText, Typography } from '@mui/material';
import IAM from '../assets/Images/services/microsoftultra.png';
import CustomerStories from './CustomerStories';
import { workCaseStudy } from '../interface/WorkCaseStudy';
import workJson from "../Work.json";
import Footer from '../components/Footer';
import { serviceWorkFilter } from '../common-functions/serviceWorkFilter';
import { CardData, TabData } from '../interface/TabData';
import ServiceTabbingSection from '../sub-sections/ServiceCommonComponent/ServiceTabbingSection';
import ServiceCardSection from '../sub-sections/ServiceCommonComponent/ServiceCardSection';
import CaseStudyLetsGetStarted from '../sub-sections/CaseStudy/CaseStudyLetsGetStarted';
import useMetaTags from '../common-functions/useMetaTags';

const IAMSection: React.FC = () => {
    useMetaTags(
        "Identity And Access Management (IAM) Solutions | Secure Cloud & Enterprise Access",
        "Protect your enterprise with advanced Identity & Access Management (IAM) solutions. Implement SSO, MFA, RBAC, and Zero Trust security for seamless, secure authentication and compliance.",
        "Identity and Access Management, IAM security solutions, Single Sign-On (SSO), Multi-Factor Authentication (MFA), Role-Based Access Control (RBAC), Cloud IAM, Zero Trust Security, Privileged Identity Management (PIM), Conditional Access, Compliance And Risk Management"
    );
    const tabData: TabData[] = [
        {
            heading: (<>Microsoft EntraUnity</>),
            imgSrc: `
            <img class="animation-img-class animation-delay300 disnone img-responsive wow fadeInDown animated fadeinScroll" 
            src="/assets/Images/iam/IdentityandAccessControl.png" alt="sso-authentication" data-wow-duration="1500ms" data-wow-delay="300ms" 
            style="display: block;">`,
            content: (<>
                <p>Unified Identity for Everyone — Employees, Partners, and Customers.</p>
                <p>We deliver secure, seamless access experiences for your entire digital ecosystem with Microsoft Entra ID and Entra External ID.</p>
                <p>Whether you're connecting employees to internal apps or enabling partners and customers to interact with your services, we help you manage every identity with confidence.</p>
                <p>Our solution empowers your business to enhance productivity, strengthen security, and build trust across your workforce and external audiences—all from a unified, enterprisegrade identity platform.</p>
            </>),
        },
        // {
        //     heading: (<>Microsoft EntraPermit</>),
        //     imgSrc: `
        //     <img class="animation-img-class animation-delay300 disnone img-responsive wow fadeInDown animated fadeinScroll" 
        //     src="/assets/Images/iam/Authentication&Security.png" alt="mfa-security" data-wow-duration="1500ms" data-wow-delay="300ms" 
        //     style="display: block;">`,
        //     content: (<>
        //         <p>We Help You Take Control of Cloud Permissions – Securely and Seamlessly.</p>
        //         <p>We empower enterprises to take control of complex multi-cloud environments with Microsoft Entra Permissions Management. </p>
        //         <p>With centralized dashboards, continuous monitoring, and automated remediation, we help your teams reduce exposure and improve security posture—without slowing down innovation. </p>
        //         <p>Partner with us to simplify permissions management and stay one step ahead of identitybased threats in the cloud.</p>
        //     </>),
        // },
        {
            heading: (<>Microsoft EntraTrust</>),
            imgSrc: `
            <img class="animation-img-class animation-delay300 disnone img-responsive wow fadeInDown animated fadeinScroll" 
            src="/assets/Images/iam/IntegrationCompliance.png" alt="rbac-pam" data-wow-duration="1500ms" data-wow-delay="300ms" 
            style="display: block;">`,
            content: (<>
                <p>Identity without Limits — For People and Workloads Alike.</p>
                <p>We secure every identity in your ecosystem with Microsoft Entra Workload ID and Verified ID, helping you protect applications, services, containers, and people through a unified, identity-first approach. </p>
                <p>We eliminate hardcoded credentials, automate access governance for non-human workloads, and enforce least-privilege principles across hybrid and multi-cloud environments.</p>
                <p>Whether you're securing back-end services or enabling secure onboarding and verification, we help you build a future where every identity is trusted, portable, and protected.</p>
            </>),
           
        },
        {
            heading: (<>Microsoft EntraConnect</>),
            imgSrc: `
            <img class="animation-img-class animation-delay300 disnone img-responsive wow fadeInDown animated fadeinScroll" 
            src="/assets/Images/iam/MicrosoftEntraPrivateAccessandInternetAccess..png" alt="identity-governance" data-wow-duration="1500ms" data-wow-delay="300ms" 
            style="display: block;">`,
            content: (<>
                <p>Connected. Protected. Everywhere.</p>
                <p>We deliver seamless, secure access to every app and every internet destination with Microsoft Entra Private and Internet Access.</p>
                <p>Whether your teams are working from home, in the office, or on the move, we ensure fast, reliable, and Zero Trust-enforced connectivity.</p>
                <p>By securing both private and public access paths, we protect identities, applications, and data from edge to cloud, all while simplifying the user experience and enhancing performance across your workforce.</p>
            </>),
        },
        {
            heading: (<>Microsoft EntraGuard</>),
            imgSrc: `
            <img class="animation-img-class animation-delay300 disnone img-responsive wow fadeInDown animated fadeinScroll" 
            src="/assets/Images/iam/MicrosofEntraIDGovernance.png" alt="identity-governance" data-wow-duration="1500ms" data-wow-delay="300ms" 
            style="display: block;">`,
            content: (<>
                <p>Smarter Identity. Stronger Control.</p>
                <p>We protect and govern identities at every stage of the digital lifecycle with Microsoft Entra ID Governance and ID Protection. From onboarding to offboarding, we ensure that access is intelligent, compliant, and adaptive.</p>
                <p>We stop threats before they spread, simplify audits, and make identity your most strategic security layer. With dynamic access reviews, just-in-time permissions, and proactive breach defense, we give you clarity and control over who can access what—when, and why.</p>
            </>),
        }
    ];    

    const cardsData: CardData[] = [
        {
            "heading": "Financial Services & Banking ",
            "items": [
                "Zero Trust Access", 
                "Secure Web Browsing", 
                "Access Reviews ",
                "Risk Detection ",
                "Verified Identity", 
                "VPN-Free Access",
            ]
        },
        {
            "heading": "Healthcare & Life Sciences",
            "items": [
                "EMR App Access", 
                "Safe SaaS Use ",
                "Role-Based Access ",
                "Threat Alerts ",
                "Staff Credentials ",
                "HIPAA Compliance "
            ]
        },
        {
            "heading": "Government & Public Sector",
            "items": [
                "Remote App Access ",
                "Web Protection ",
                "Policy Controls ",
                "Risk-Based Blocks ",
                "Citizen ID Proofing ",
                "Full Audit Trails "
            ]
        },
        {
            "heading": "Education & EdTech",
            "items": [
                "Campus App Access ",
                "Secure Web Use ",
                "Student Lifecycle ",
                "Login Risk Alerts ",
                "Digital Transcripts", 
                "FERPA Compliance "
            ]
        }
    ];

    const [works, setWorks] = useState<workCaseStudy[]>([]);
    useEffect(() => {
        const keywords = ['azure', 'aws', 'cloud'];
        const additionalKeywords = ['ai', 'artificialintelligence', 'machinelearning', 'automation', 'deeplearning', 'datascience', 'technology', 'microsoft'];
        const results = serviceWorkFilter(workJson.workCaseStudy, keywords, additionalKeywords);
        setWorks(results);
    }, []);
    return (
        <>
            <Box sx={{ padding: 4, textAlign: 'center' }} className="contentbox whitebox ">
                <Container className="innerpagecontainer contentpage">
                    <Typography variant="h2" gutterBottom className='contentwidth to_build'>
                    Advanced IAM Solutions for Secure Authentication, Access Control, and Regulatory Compliance</Typography>

                    <Typography variant="body1" sx={{ marginBottom: 2 }} className='contentwidth'>
                    Streamline user access, reduce operational risk, and enhance productivity with our tailored IAM services built for modern enterprise demands </Typography>

                    <div className='servicesLogo'>
                        <ul>
                            <li><span>
                                <img
                                height="60px"
                                style={{height:"60px"}}
                                    src={IAM}
                                    alt="Advanced IAM Solutions for Secure Authentication"
                                /></span></li>
                        </ul>
                    </div>


                </Container>
            </Box>

            <Box className="outerbox powerbitab" sx={{ bgcolor: "#f2c811" }}  >
                <ServiceTabbingSection tabData={tabData} />
            </Box>
            <Box sx={{ padding: 4 }} className="contentbox whitebox contentpage"  >
                <Container className="innerpagecontainer contentpage" >
                    <ServiceCardSection cardsData={cardsData} verticalTitle='Transforming Industries with Tailored IAM Solutions across Verticals' />
                </Container>
            </Box>

            {/* <ServiceCarousel category="azureAWSCloud" /> */}
            {works.length > 0 &&
                <CustomerStories pageName='service' workList={works} />
            }
            <CaseStudyLetsGetStarted projectName='Azure AWS Service' />
            {works.length > 0 ?
                <Footer hasWorkItem={false} /> : <Footer hasWorkItem={true} />
            }
        </>
    );
};

export default IAMSection;